import {
  AudienceCriterionState,
  DraftEntryResponseOption,
  ResponseOptionType,
  TextEntryState
} from '../../data/gql-gen/questionnaire/graphql'
import {
  MarketAudienceMember,
  MarketAudiencesData
} from '../../data/gql/questionnaire/queries/marketAudiences'
import {
  FieldworkAudience,
  FieldworkAudienceType
} from '../../data/model/audience'
import {
  DraftQuestionnaireEntry,
  EntryType,
  QuestionKind,
  QuestionTypeCode
} from '../../data/model/questionnaire'
import { getSourceQuestions } from '../Audience/audienceConditions'
import {
  getAudienceMemberQuestion,
  getAudienceMemberQuestionId,
  inScreenerOrder,
  isHiddenFromSurveyQuestion
} from '../Audience/audienceOrder'
import { useProjectType } from '../Project/Project.hooks'
import { isHiddenIn } from './MarketVisibility/marketVisibility'
import { MarketOption } from './MarketVisibility/useProjectMarkets'
import { MarketAudience, useMarketAudience } from './marketAudienceContext'

// Multi-market projects author an audience per market. Display logic lives on
// the shared questionnaire and is stored as plain (questionLk, responseOptionLk)
// ids, so a rule on an audience question holds only in the markets whose
// audience asks that question with that option. Matching is therefore on ids
// alone: question text is stored per market in that market's own language, so
// it says nothing about whether two markets mean the same question.

export interface MarketAudienceOption {
  id: string
  value: string
  markets: Set<string>
}

export interface MarketAudienceQuestion {
  questionId: string
  label: string
  isStandard: boolean
  contextPosition: number
  markets: Set<string>
  options: MarketAudienceOption[]
}

export interface MarketAudienceIndex {
  marketIds: string[]
  questions: Map<string, MarketAudienceQuestion>
}

export interface MarketAudienceMembers {
  surveyId: string
  members: MarketAudienceMember[]
}

// A disabled custom question is never asked, and respondents could never satisfy
// a rule referencing it; getSourceQuestions drops the hidden standard criteria.
const askableMembers = (members: MarketAudienceMember[]): FieldworkAudience[] =>
  members.filter(
    (member) =>
      member.memberType === FieldworkAudienceType.Standard ||
      (!!member.memberItem.questionLk &&
        !!member.memberItem.question &&
        member.memberItem.state !== AudienceCriterionState.Disabled)
  ) as FieldworkAudience[]

const memberLabel = (member: FieldworkAudience): string =>
  member.memberType === FieldworkAudienceType.Standard
    ? member.memberItem.criterion.name
    : getAudienceMemberQuestion(member).text

/**
 * The audience questions respondents are asked in each market, merged by
 * question id. The current market goes first, so its order, numbering and text
 * win for a question several markets ask.
 */
export const buildMarketAudienceIndex = (
  markets: MarketAudienceMembers[],
  currentSurveyId?: string
): MarketAudienceIndex => {
  const questions = new Map<string, MarketAudienceQuestion>()
  const ordered = [
    ...markets.filter((market) => market.surveyId === currentSurveyId),
    ...markets.filter((market) => market.surveyId !== currentSurveyId)
  ]

  for (const { surveyId, members } of ordered) {
    const asked = askableMembers(members)
    const memberByQuestionId = new Map(
      asked.map((member) => [getAudienceMemberQuestionId(member), member])
    )
    const screenerPositions = new Map(
      inScreenerOrder(asked)
        .filter((member) => !isHiddenFromSurveyQuestion(member))
        .map((member, position) => [
          getAudienceMemberQuestionId(member),
          position
        ])
    )

    for (const source of getSourceQuestions(asked)) {
      const member = memberByQuestionId.get(source.questionId)
      if (!member) {
        continue
      }

      let question = questions.get(source.questionId)
      if (!question) {
        question = {
          questionId: source.questionId,
          label: memberLabel(member),
          isStandard: source.isTargeting,
          contextPosition: screenerPositions.get(source.questionId) ?? 0,
          markets: new Set(),
          options: []
        }
        questions.set(source.questionId, question)
      }
      question.markets.add(surveyId)

      for (const { id, value } of source.responseOptions) {
        const option = question.options.find((known) => known.id === id)
        if (option) {
          option.markets.add(surveyId)
        } else {
          question.options.push({ id, value, markets: new Set([surveyId]) })
        }
      }
    }
  }

  return { marketIds: markets.map((market) => market.surveyId), questions }
}

// Audience entries are numbered above every questionnaire entry, so a lookup by
// number (a route's target) never lands on one.
const AUDIENCE_ENTRY_NUMBER_OFFSET = 9000

const marketSuffix = (
  markets: ReadonlySet<string>,
  marketLabels: ReadonlyMap<string, string>
): string => {
  const labels = [...marketLabels]
    .filter(([surveyId]) => markets.has(surveyId))
    .map(([, label]) => label)
  return labels.length > 0 ? ` (${labels.join(', ')})` : ''
}

const toResponseOption = (
  option: MarketAudienceOption,
  position: number,
  suffix: string
): DraftEntryResponseOption => ({
  augmentations: [],
  marketIds: [],
  position,
  createdDate: '',
  sinceDate: '',
  responseOptionLk: option.id,
  responseOption: {
    responseOptionId: option.id,
    rotype: ResponseOptionType.RoString,
    value: `${option.value}${suffix}`,
    createdDate: '',
    __typename: 'ResponseOption'
  },
  maskingRules: [],
  textEntryState: TextEntryState.TextEntryDisabled,
  __typename: 'DraftEntryResponseOption'
})

const questionSuffix = (
  question: MarketAudienceQuestion,
  { marketIds }: MarketAudienceIndex,
  marketLabels: ReadonlyMap<string, string>
): string =>
  question.markets.size < marketIds.length
    ? marketSuffix(question.markets, marketLabels)
    : ''

const optionSuffix = (
  option: MarketAudienceOption,
  question: MarketAudienceQuestion,
  marketLabels: ReadonlyMap<string, string>
): string =>
  option.markets.size < question.markets.size
    ? marketSuffix(option.markets, marketLabels)
    : ''

/**
 * The index as audience entries shaped like the questionnaire's own. A question
 * some market doesn't ask carries the markets that do ("Region (United
 * Kingdom)"), and so does an option some market asking its question lacks
 * ("18-29 (Germany)").
 */
export const toAudienceEntries = (
  index: MarketAudienceIndex,
  marketLabels: ReadonlyMap<string, string>
): DraftQuestionnaireEntry<EntryType.QuestionEntryType>[] =>
  [...index.questions.values()].map((question, position) => ({
    id: question.questionId,
    entryId: question.questionId,
    sectionId: undefined,
    number: AUDIENCE_ENTRY_NUMBER_OFFSET + position,
    position,
    contextPosition: question.contextPosition,
    questionKind: QuestionKind.AudienceKind,
    forks: [],
    createdDate: '',
    sinceDate: '',
    loopSpecificDisplayLogic: null,
    entryType: EntryType.QuestionEntryType,
    entryItem: {
      questionTypeCode: question.isStandard
        ? QuestionTypeCode.StandardAudience
        : QuestionTypeCode.CustomAudience,
      questionLk: question.questionId,
      question: {
        questionId: question.questionId,
        text: `${question.label}${questionSuffix(
          question,
          index,
          marketLabels
        )}`,
        createdDate: '',
        __typename: 'Question'
      },
      questionLogic: [],
      responseOptions: question.options.map((option, optionPosition) =>
        toResponseOption(
          option,
          optionPosition,
          optionSuffix(option, question, marketLabels)
        )
      ),
      maxDiffSpecification: null,
      settingValues: [],
      __typename: 'DraftQuestionItem'
    },
    __typename: 'DraftQuestionnaireEntry'
  }))

export const toMarketAudience = (
  data: MarketAudiencesData | undefined,
  currentSurveyId: string,
  markets: MarketOption[]
): MarketAudience => {
  // A market removed from a tracker wave stays in the wave's aggregate.
  const inScope = new Set(markets.map((market) => market.surveyId))
  const index = buildMarketAudienceIndex(
    (data?.questionnaireAggV2?.marketsSpecification ?? [])
      .filter(({ key }) => inScope.has(key))
      .map(({ key, value }) => ({
        surveyId: key,
        members: value.draftAudience.members
      })),
    currentSurveyId
  )
  // Named as the Audiences tabs name them: one country can hold several markets.
  const marketLabels = new Map(
    markets.map((market) => [market.surveyId, market.label])
  )

  return {
    status: 'ready',
    index,
    marketLabels,
    entries: toAudienceEntries(index, marketLabels)
  }
}

/**
 * `entries` plus every market's audience questions, for resolving logic
 * references only — never validate, save, number or render the result as
 * questions. An audience entry keeps its own text and number, gains the market
 * suffix, and is given the options only other markets offer.
 */
export const withMarketAudienceEntries = (
  entries: DraftQuestionnaireEntry[],
  { index, marketLabels, entries: marketAudienceEntries }: MarketAudience
): DraftQuestionnaireEntry[] => {
  if (marketAudienceEntries.length === 0) {
    return entries
  }
  const held = new Set<string>()
  const labelled = entries.map((entry) => {
    if (
      entry.questionKind !== QuestionKind.AudienceKind ||
      entry.entryType !== EntryType.QuestionEntryType
    ) {
      return entry
    }
    const question = index.questions.get(entry.entryItem.questionLk)
    if (!question) {
      return entry
    }
    held.add(question.questionId)
    const { question: text, responseOptions } = entry.entryItem
    const optionLks = new Set(
      responseOptions.map((option) => option.responseOptionLk)
    )
    return {
      ...entry,
      entryItem: {
        ...entry.entryItem,
        question: text && {
          ...text,
          text: `${text.text}${questionSuffix(question, index, marketLabels)}`
        },
        responseOptions: [
          ...responseOptions.map((responseOption) => {
            const option = question.options.find(
              ({ id }) => id === responseOption.responseOptionLk
            )
            const suffix = option
              ? optionSuffix(option, question, marketLabels)
              : ''
            return suffix && responseOption.responseOption
              ? {
                  ...responseOption,
                  responseOption: {
                    ...responseOption.responseOption,
                    value: `${responseOption.responseOption.value}${suffix}`
                  }
                }
              : responseOption
          }),
          ...question.options
            .filter((option) => !optionLks.has(option.id))
            .map((option, position) =>
              toResponseOption(
                option,
                responseOptions.length + position,
                optionSuffix(option, question, marketLabels)
              )
            )
        ]
      }
    }
  })
  return [
    ...labelled,
    ...marketAudienceEntries.filter(
      (entry) => !held.has(entry.entryItem.questionLk)
    )
  ]
}

export type LogicFlavour = 'displayLogic' | 'masking'

export interface LogicLiteral {
  questionLk: string
  responseOptionLk: string
  negated: boolean
  matrixRowLk?: string
}

type EntriesById = ReadonlyMap<string, DraftQuestionnaireEntry>

const byEntryId = (entries: DraftQuestionnaireEntry[]): EntriesById => {
  const entriesById = new Map<string, DraftQuestionnaireEntry>()
  for (const entry of entries) {
    if (!entriesById.has(entry.entryId)) {
      entriesById.set(entry.entryId, entry)
    }
  }
  return entriesById
}

const sourceOptionHiddenIn = (
  source: DraftQuestionnaireEntry,
  responseOptionLk: string
): string[] | null | undefined =>
  source.entryType === EntryType.QuestionEntryType ||
  source.entryType === EntryType.MatrixEntryType
    ? source.entryItem.responseOptions.find(
        (option) => option.responseOptionLk === responseOptionLk
      )?.hiddenInMarkets
    : undefined

const marketsWhereHolds = (
  clauses: LogicLiteral[][],
  flavour: LogicFlavour,
  { marketIds, questions }: MarketAudienceIndex,
  entriesById: EntriesById
): Set<string> => {
  const literalMarkets = ({
    questionLk,
    responseOptionLk,
    negated,
    matrixRowLk
  }: LogicLiteral): Iterable<string> => {
    if (negated && flavour === 'masking') {
      return marketIds
    }
    const question = questions.get(questionLk)
    if (question) {
      return negated
        ? question.markets
        : question.options.find((option) => option.id === responseOptionLk)
            ?.markets ?? []
    }
    const source = entriesById.get(questionLk)
    if (!source) {
      return []
    }
    if (negated && source.entryType === EntryType.MatrixEntryType) {
      return marketIds
    }
    const optionHiddenIn = negated
      ? undefined
      : sourceOptionHiddenIn(source, responseOptionLk)
    const rowHiddenIn =
      source.entryType === EntryType.MatrixEntryType
        ? source.entryItem.matrixRows.find(
            (row) => row.questionLk === matrixRowLk
          )?.hiddenInMarkets
        : undefined
    return marketIds.filter(
      (surveyId) =>
        !isHiddenIn(source.hiddenInMarkets, surveyId) &&
        !isHiddenIn(optionHiddenIn, surveyId) &&
        !isHiddenIn(rowHiddenIn, surveyId)
    )
  }

  return clauses.reduce((markets, clause) => {
    const clauseMarkets = new Set(
      clause.flatMap((literal) => [...literalMarkets(literal)])
    )
    return new Set(
      [...markets].filter((surveyId) => clauseMarkets.has(surveyId))
    )
  }, new Set(marketIds))
}

/**
 * The markets where logic can hold: literals ORed within a clause, clauses
 * ANDed. Display logic fails on an unanswered question before negation, so a
 * negated literal holds only where its question is asked; masking, and display
 * logic on a matrix, negate after, so a negated literal holds everywhere.
 */
export const conditionMarkets = (
  clauses: LogicLiteral[][],
  flavour: LogicFlavour,
  index: MarketAudienceIndex,
  entries: DraftQuestionnaireEntry[]
): Set<string> => marketsWhereHolds(clauses, flavour, index, byEntryId(entries))

export type LogicReach = (
  clauses: LogicLiteral[][],
  flavour: LogicFlavour,
  hiddenInMarkets: string[]
) => boolean

/**
 * Whether logic can hold in a market the item it governs is shown in; undefined
 * until every market's audience resolves.
 */
export const marketReach = (
  index: MarketAudienceIndex,
  entries: DraftQuestionnaireEntry[]
): LogicReach | undefined => {
  if (index.marketIds.length === 0) {
    return undefined
  }
  const entriesById = byEntryId(entries)
  return (clauses, flavour, hiddenInMarkets) => {
    const shown = index.marketIds.filter(
      (surveyId) => !hiddenInMarkets.includes(surveyId)
    )
    const markets = marketsWhereHolds(clauses, flavour, index, entriesById)
    return shown.length === 0 || shown.some((surveyId) => markets.has(surveyId))
  }
}

/**
 * The markets an item hidden in `hiddenInMarkets` is shown in; every market
 * when it is shown in none, as `marketReach` leaves such an item alone.
 */
export const shownInMarkets = (
  marketIds: string[],
  hiddenInMarkets: string[] | null | undefined
): string[] => {
  const shown = marketIds.filter(
    (surveyId) => !isHiddenIn(hiddenInMarkets, surveyId)
  )
  return shown.length > 0 ? shown : marketIds
}

/**
 * Every market's audience entries asked in one of `markets`, each narrowed to
 * the options asked in one of them. `referencedLks` keeps the question and the
 * options a rule already points at, so a pre-existing rule stays editable.
 */
export const forVisibleMarkets = (
  { index, entries }: Pick<MarketAudience, 'index' | 'entries'>,
  markets: readonly string[],
  referencedLks: ReadonlySet<string> = new Set()
): DraftQuestionnaireEntry<EntryType.QuestionEntryType>[] => {
  const isAsked = (askedIn: ReadonlySet<string> | undefined) =>
    markets.some((surveyId) => askedIn?.has(surveyId))

  return entries.flatMap((entry) => {
    const { questionLk, responseOptions } = entry.entryItem
    const question = index.questions.get(questionLk)
    if (
      !question ||
      (!isAsked(question.markets) && !referencedLks.has(questionLk))
    ) {
      return []
    }
    const asked = responseOptions.filter(
      ({ responseOptionLk }) =>
        referencedLks.has(responseOptionLk) ||
        isAsked(
          question.options.find((option) => option.id === responseOptionLk)
            ?.markets
        )
    )
    return asked.length === responseOptions.length
      ? [entry]
      : [
          {
            ...entry,
            entryItem: { ...entry.entryItem, responseOptions: asked }
          }
        ]
  })
}

export interface AudienceLogicScope {
  hasMarkets: boolean
  marketAudience: MarketAudience
  /** The markets the item the logic governs is shown in. */
  markets: string[]
}

/**
 * Every market's audience, scoped to the markets an item hidden in
 * `hiddenInMarkets` is shown in. Empty in a project without markets.
 */
export const useAudienceLogicScope = (
  hiddenInMarkets: string[] | null | undefined
): AudienceLogicScope => {
  const { hasMarkets } = useProjectType()
  const marketAudience = useMarketAudience({ skip: !hasMarkets })
  return {
    hasMarkets,
    marketAudience,
    markets: shownInMarkets(marketAudience.index.marketIds, hiddenInMarkets)
  }
}

export const MARKET_AUDIENCE_LOAD_FAILED_ERROR =
  "Couldn't load every market's audience questions. Reload the page to edit this rule."

/**
 * A logic dialog holds Save while every market's audience loads; once that
 * fails, a rule on a question it can't resolve must not be saved over.
 */
export const marketAudienceGate = ({
  hasMarkets,
  marketAudience: { status }
}: AudienceLogicScope): {
  awaitingMarkets: boolean
  marketsFailed: boolean
} => ({
  awaitingMarkets: hasMarkets && status === 'loading',
  marketsFailed: hasMarkets && status === 'failed'
})

/**
 * The questions a logic picker offers. A project with markets swaps the current
 * market's audience questions for every market's asked where the item is shown
 * — none until every market's audience resolves, so a picker never offers one
 * that silently never matches. A current-market question in `referencedLks` not
 * offered otherwise stays, narrowed to the referenced options.
 */
export const pickerEntries = (
  entries: DraftQuestionnaireEntry[],
  { hasMarkets, marketAudience, markets }: AudienceLogicScope,
  referencedLks: ReadonlySet<string> = new Set()
): DraftQuestionnaireEntry[] => {
  if (!hasMarkets) {
    return entries
  }
  const offered = forVisibleMarkets(marketAudience, markets, referencedLks)
  const offeredLks = new Set(offered.map((entry) => entry.entryItem.questionLk))
  return [
    ...offered,
    ...entries.flatMap((entry) => {
      if (entry.questionKind !== QuestionKind.AudienceKind) {
        return [entry]
      }
      if (
        entry.entryType !== EntryType.QuestionEntryType ||
        !referencedLks.has(entry.entryItem.questionLk) ||
        offeredLks.has(entry.entryItem.questionLk)
      ) {
        return []
      }
      return [
        {
          ...entry,
          entryItem: {
            ...entry.entryItem,
            responseOptions: entry.entryItem.responseOptions.filter(
              ({ responseOptionLk }) => referencedLks.has(responseOptionLk)
            )
          }
        }
      ]
    })
  ]
}

// Whether display logic has any audience question to reference — the first
// question of a survey can only carry display logic through one.
export const offersAudienceQuestion = (
  entries: DraftQuestionnaireEntry[],
  scope: AudienceLogicScope
): boolean =>
  pickerEntries(entries, scope).some(
    (entry) => entry.questionKind === QuestionKind.AudienceKind
  )
